import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { auth, type DemoPersona } from './auth';

export interface Client {
  id: string;
  name: string;
  legalName: string | null;
  trn: string | null;
  baseCurrency: string;
  country: string;
  emirate: string | null;
  /** 'uae' (VAT) or 'in' (India GST). */
  taxPack?: string;
  gstin?: string | null;
  stateCode?: string | null;
  gstRegistrationType?: string | null;
  fiscalYearStart: number;
  taxPeriod: 'monthly' | 'quarterly';
  chartTemplate: string | null;
  /** Arabic switched on for this client (document 15) and its default language. */
  arabicEnabled?: boolean;
  defaultLanguage?: 'en' | 'ar';
  role: string;
  accountants: string[];
  createdAt: string;
}

export interface Me {
  userId: string;
  email: string;
  name: string;
  /** Profile and security (document 06 G14). */
  phone: string | null;
  /** The language the app speaks to this user (document 15, D2). */
  language?: 'en' | 'ar';
  roles: string[];
  isStaff: boolean;
}

export function useClients(enabled = true) {
  return useQuery({
    queryKey: ['practice', 'clients'],
    queryFn: () => api<Client[]>('/practice/clients'),
    enabled,
    staleTime: 60_000,
  });
}

export function useMe(enabled = true) {
  return useQuery({
    queryKey: ['practice', 'me'],
    queryFn: () => api<Me>('/practice/me'),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** Profile and security (G14): the user's own name and phone. */
export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name?: string; phone?: string | null; language?: 'en' | 'ar' }) =>
      api<Me>('/practice/me', { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: (me) => {
      qc.setQueryData(['practice', 'me'], me);
      void qc.invalidateQueries({ queryKey: ['practice', 'clients'] });
    },
  });
}

/**
 * The public demo (ADR-0020): its personas post in sample companies shared with other visitors;
 * the API refuses only their changes to users, settings and connections.
 */
export function useIsDemo(): boolean {
  return useDemoPersona() !== null;
}

/** Which demo persona is signed in: the accountant, the business owner, or null (not the demo). */
export function useDemoPersona(): DemoPersona | null {
  const roles = useMe(auth.enabled).data?.roles;
  if (!roles?.includes('demo')) return null;
  return roles.includes('client_owner') ? 'owner' : 'accountant';
}

export const ROLE_LABELS: Record<string, string> = {
  outss_admin: 'Admin',
  outss_accountant: 'Accountant',
  client_owner: 'Client owner',
  client_viewer: 'Client viewer',
};

/** TRN grouped for display: 100 1234567 00 003 (document 05, Formats). */
export function formatTrn(trn: string | null): string {
  if (!trn) return '';
  return trn.length === 15
    ? `${trn.slice(0, 3)} ${trn.slice(3, 10)} ${trn.slice(10, 12)} ${trn.slice(12)}`
    : trn;
}
